import { useEffect, useState } from "react";
import { Footer } from "@/components/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Shield, Lock, ChevronLeft } from "lucide-react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { EmailLink } from "@/components/EmailLink";

const faqs = [
  {
    question: "Is my data used to train AI models?",
    answer: "No. Message content we send to Google Gemini is used only to produce your result, and ClearMakes doesn't use your data to train AI models."
  },
  {
    question: "How does ClearMakes follow Google's Limited Use requirements?",
    answer: "We follow the Google API Services User Data Policy, including the Limited Use requirements, also when the data is processed by Composio or Google Gemini on our behalf. Google data is used only to run your workflows and Smart FAQ, and is never sold or used for advertising."
  },
  {
    question: "How do I delete my data?",
    answer: "Use Delete my account in Settings in ClearMakes Inbox, or email contact@clearmakes.com. We disconnect your apps straight away and delete your data within 30 days."
  }
];

const Privacy = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // FAQ Schema Markup (JSON-LD)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
          <HelmetProvider>
        <Helmet>
          <title>Privacy Policy | ClearMakes</title>
          <meta name="description" content="What data ClearMakes collects, how we use it, who we share it with and the choices you have about it, written in plain words so anyone can follow it." />
        </Helmet>
      </HelmetProvider>
<div className="min-h-screen bg-[#0A0F1C] text-foreground font-sans flex flex-col relative selection:bg-primary/30">
      {/* Inject JSON-LD Schema directly into the component */}
      
        
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "WebSite",
                  "@id": "https://clearmakes.com/#website",
                  "url": "https://clearmakes.com/",
                  "name": "ClearMakes",
                  "description": "The Professional Workspace for unified messaging and AI communication.",
                  "publisher": { "@id": "https://clearmakes.com/#organization" }
                },
                {
                  "@type": "Organization",
                  "@id": "https://clearmakes.com/#organization",
                  "name": "ClearMakes",
                  "url": "https://clearmakes.com/",
                  "logo": { "@type": "ImageObject", "url": "https://clearmakes.com/logo.png" },
                  "sameAs": [
                    "https://github.com/ClearMakes"
                  ]
                },
                {
                  "@type": "SoftwareApplication",
                  "@id": "https://clearmakes.com/#software",
                  "name": "ClearMakes Workspace",
                  "applicationCategory": "BusinessApplication",
                  "operatingSystem": "Web",
                  "url": "https://clearmakes.com/",
                  "offers": { "@type": "Offer", "price": "0.00", "priceCurrency": "USD" }
                }
              ]
            })
          }}
        />

      <div className="absolute inset-0 noise-texture opacity-20 pointer-events-none" />
      

      <main className="flex-grow pt-32 pb-20 container relative z-10 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6 border border-primary/20">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white tracking-tight">Privacy Policy</h1>
          <p className="text-lg text-muted-foreground">How ClearMakes collects, uses, and protects your data.</p>
          <p className="text-sm text-gray-500 mt-2 font-mono uppercase tracking-widest">Last Updated: October 24, 2026</p>
        </div>

        <div className="space-y-10 text-gray-300 glass-panel border border-white/5 rounded-[2.5rem] p-8 md:p-16">

          {/* Section 1: Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              This Privacy Policy explains what data ClearMakes collects, how we use it, who we share it with and the
              choices you have. It covers clearmakes.com and <a href="/products/inbox" className="text-primary hover:underline">ClearMakes Inbox</a>,
              including the apps you connect to it and our use of Google API Services.
            </p>
          </section>

          {/* Section 2: Data Accessed */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Data We Access and Collect</h2>
            <ul className="list-disc pl-5 space-y-3 mb-4 text-lg text-muted-foreground">
              <li>
                <strong className="text-white">Account information:</strong> your name, email address and profile picture.
                If you sign in with Google or Microsoft, we receive only your email address and basic profile.
              </li>
              <li>
                <strong className="text-white">Data from the apps you connect:</strong> the messages, events and files your
                workflows and Smart FAQ need, as described in section 2a.
              </li>
              <li>
                <strong className="text-white">What you create:</strong> your workflows, Smart FAQ answers, and the results of
                each workflow run.
              </li>
              <li>
                <strong className="text-white">Billing:</strong> your plan and payment status. Paddle handles the payment
                itself; we never see or store your card number.
              </li>
              <li id="invites">
                <strong className="text-white">Invites:</strong> if you join through a friend&apos;s invite link, which link
                it was and when, and whether you paid for a plan. Your friend sees only how many people joined, never who.
                To check that nobody invites themselves, we compare the paying account, its email and its card (Paddle
                shows us only the card&apos;s last 4 digits, expiry and name, and we delete these once the check is done).
                An invite&apos;s record is deleted 2 years after it is settled.
              </li>
              <li>
                <strong className="text-white">Technical data:</strong> basic logs such as IP address, browser and error
                reports, used to keep the service running and secure.
              </li>
            </ul>
          </section>

          {/* Section 2a: connected apps */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2a. Connected Apps</h2>
            <p className="mb-4 leading-relaxed text-lg text-muted-foreground">
              <strong className="text-white">Connected apps.</strong> When you connect an app (for example Gmail, Outlook,
              Slack, Microsoft Teams, Google Calendar, Drive, Sheets, Notion, HubSpot or Linear), you sign in through
              Composio, our integration provider, and grant the permissions that app asks for. ClearMakes then reads the
              messages, events and files your workflows and Smart FAQ need, and sends or creates only what your workflows
              tell it to.
            </p>
            <p className="leading-relaxed text-lg text-muted-foreground">
              Connecting an app is optional. You can disconnect any app at any time from the Apps page in ClearMakes Inbox,
              and for Google apps also from your{" "}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="text-primary hover:underline">Google Account permissions page</a>.
            </p>
          </section>

          {/* Section 3: Data Usage */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Data</h2>
            <ul className="list-disc pl-5 space-y-3 text-lg text-muted-foreground">
              <li>To run your <a href="/workflows" className="text-primary hover:underline">workflows</a> and show you their results.</li>
              <li>To let <a href="/smart-faq" className="text-primary hover:underline">Smart FAQ</a> answer questions from the answers you wrote.</li>
              <li>To create and secure your account, handle your plan, and send account emails and notifications you turn on.</li>
              <li><strong className="text-white">To send you product news, only if you said yes</strong> (your consent): new features and launches, at most twice a month. Every email has an unsubscribe link, and you can turn product news off in your account settings at any time. We count how many people open and click, but not who.</li>
              <li>To fix problems and keep the service safe from abuse.</li>
            </ul>
            <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl flex items-start gap-4">
              <Lock className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <p className="text-white/80 text-sm leading-relaxed">
                We store the workflow runs and answers you create so you can see them. Message content we send to Google
                Gemini is used only to produce your result; ClearMakes doesn't use it to train AI models.
              </p>
            </div>
          </section>

          {/* Section 4: Data Storage & Protection */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Data Storage and Protection</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              Your data is encrypted in transit (TLS) and at rest. Access is limited to the systems that run your
              workflows, and to the few people who need it to keep the service running or to help you when you ask.
              Your data is never used to train AI models. More detail is on our{" "}
              <a href="/security-architecture" className="text-primary hover:underline">security page</a>.
            </p>
          </section>

          {/* Section 5: Data Sharing & Sub-processors */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Sharing and Sub-processors</h2>
            <p className="mb-4 leading-relaxed text-lg text-muted-foreground">
              We do not sell your personal data. We share it only with the companies below, and only so they can provide
              their part of the service. The same list is on our{" "}
              <a href="/guidelines" className="text-primary hover:underline">sub-processors page</a>.
            </p>
            <ul className="list-disc pl-5 space-y-3 mt-4 text-lg text-muted-foreground">
              <li><strong className="text-white">Contabo:</strong> hosting, in the European Union (Germany).</li>
              <li><strong className="text-white">MongoDB Atlas:</strong> database.</li>
              <li><strong className="text-white">Google:</strong> Gemini (AI that runs workflow steps and Smart FAQ), Sign in with Google, Firebase Cloud Messaging (notifications) and Google Analytics (website only).</li>
              <li><strong className="text-white">Microsoft:</strong> Sign in with Microsoft and Microsoft Clarity (website only).</li>
              <li><strong className="text-white">Composio:</strong> connects your apps and holds the connection for us.</li>
              <li><strong className="text-white">Brevo:</strong> sends account emails, such as sign-in codes and receipts, and product news if you asked for it. Our news emails are sent from our own server in Germany, through Brevo.</li>
              <li><strong className="text-white">Paddle:</strong> our reseller and merchant of record, which handles payments. We never see or store your card number.</li>
              <li><strong className="text-white">The apps you connect:</strong> receive what your workflows send or create in them.</li>
            </ul>
            <p className="mt-4 leading-relaxed text-lg text-muted-foreground">
              We may also share data when the law requires it, or to protect our users and the service.
            </p>
          </section>

          {/* Section 6: Website analytics */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Website Analytics</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              Google Analytics and Microsoft Clarity measure how the clearmakes.com website and sign-in page are used,
              for example which pages are visited and where people click. They use cookies to do this. The signed-in
              Inbox app doesn't load them. You can block these cookies in your browser settings.
            </p>
            <p className="mt-4 leading-relaxed text-lg text-muted-foreground">
              They run only if you agree in the cookie banner, and you can change your choice at any time. Our{" "}
              <a href="/cookies" className="text-primary hover:underline">Cookie page</a> lists every cookie and how long it lasts.
            </p>
            <p className="mt-4 leading-relaxed text-lg text-muted-foreground">
              If you agreed, we also keep the first link that brought you to a ClearMakes site (for example LinkedIn) for
              90 days, and when you create an account or buy a plan, our server tells Google Analytics that it happened:
              the product, the plan, the price and that first link, tied to Google Analytics&apos; random number for your
              browser. Never your name or email. If you didn&apos;t agree, nothing is sent.
            </p>
            <p className="mt-4 leading-relaxed text-lg text-muted-foreground">
              Some of our emails link to a short video page made for you on watch.clearmakes.com. When you open it,
              we record that the page was opened and how long the video played, so we know whether it was useful; it
              shows only your first name and company, and you can reply &quot;stop&quot; to any of our emails to hear
              nothing more.
            </p>
          </section>

          {/* Section 7: Retention & Deletion */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Data Retention and Deletion</h2>
            <p className="mb-4 leading-relaxed text-lg text-muted-foreground">
              <strong className="text-white">Retention:</strong> We keep your data while your account is open. You can
              delete a workflow, an answer or a connected app at any time.
            </p>
            <p className="mb-4 leading-relaxed text-lg text-muted-foreground">
              <strong className="text-white">Deleting your account:</strong> Use "Delete my account" in Settings in
              ClearMakes Inbox, or email us. We disconnect your apps straight away and delete your data from our systems
              within 30 days. Copies in backups are removed as the backups expire. We may keep billing records for as
              long as tax law requires.
            </p>
            <p className="leading-relaxed text-lg text-muted-foreground">
              To ask for deletion by email, write to <EmailLink email="contact@clearmakes.com" className="text-primary hover:underline font-medium">contact@clearmakes.com</EmailLink>.
            </p>
          </section>

          {/* Section 8: Your rights */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Your Rights</h2>
            <p className="mb-4 leading-relaxed text-lg text-muted-foreground">
              Wherever you live, you can ask us to:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-lg text-muted-foreground">
              <li>show you the personal data we hold about you (access);</li>
              <li>correct data that is wrong (correction);</li>
              <li>give you a copy of your data in a common format (export);</li>
              <li>delete your data (deletion).</li>
            </ul>
            <p className="mt-4 leading-relaxed text-lg text-muted-foreground">
              Email <EmailLink email="contact@clearmakes.com" className="text-primary hover:underline font-medium">contact@clearmakes.com</EmailLink> and
              we will reply within 30 days. If you are not happy with our answer, you can complain to the data protection
              authority where you live.
            </p>
          </section>

          {/* Section 9: International transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. International Transfers</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              Our servers and the companies in section 5 may process your data in countries other than yours, such as the
              United States. When we move personal data out of the European Economic Area or the United
              Kingdom, we rely on the safeguards those companies offer, such as the European Commission's Standard
              Contractual Clauses.
            </p>
          </section>

          {/* Section 10: Children */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Children</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              ClearMakes is not for anyone under 18, and we do not knowingly collect data from children. If you believe a
              child has given us data, email us and we will delete it.
            </p>
          </section>

           {/* Section 11: Google Limited Use */}
           <section className="bg-white/5 p-8 rounded-2xl border border-white/10">
            <h2 className="text-2xl font-semibold text-white mb-4">11. Google Limited Use Disclosure</h2>
            <p className="leading-relaxed text-lg text-white/80">
              ClearMakes's use and transfer to any other app of information received from Google APIs will adhere to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements, including when the data is processed by Composio or Google Gemini
              on our behalf.
            </p>
            <ul className="list-disc pl-5 space-y-3 text-lg text-white/80">
              <li>We use Google user data only to run the workflows and Smart FAQ you have set up.</li>
              <li>We do not transfer Google user data to others, except as needed to run those features (Composio and Google Gemini process it on our behalf), for security purposes, or to comply with the law.</li>
              <li>We do not use Google user data for advertising, and we never sell it.</li>
              <li>No person reads your Google user data, unless you ask us to, it is necessary for security or to comply with the law, or the data has been aggregated and made anonymous.</li>
              <li>We do not use Google user data to develop, improve or train generalised AI or machine-learning models.</li>
            </ul>
          </section>

          {/* Section 12: Changes */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              If we make an important change, we will update the date at the top of this page and tell signed-in users by
              email or in the app.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              If you have any questions about this Privacy Policy, email{" "}
              <EmailLink email="contact@clearmakes.com" className="text-primary hover:underline font-medium">
                contact@clearmakes.com
              </EmailLink>.
            </p>
          </section>
        </div>

        {/* FAQ SCHEMA SECTION (Strict Requirement) */}
        <section className="mt-20 max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">Privacy & Security FAQs</h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] rounded-2xl overflow-hidden transition-colors duration-300">
                <button
                  className="w-full px-8 py-6 text-left flex justify-between items-center focus:outline-none"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                >
                  <span className="font-semibold text-white pr-8 text-lg">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-primary flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence initial={false}>
                  {openFaqIndex === index && (
                    <motion.section
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-8 text-muted-foreground leading-relaxed border-t border-white/5 pt-6">
                        {faq.answer}
                      </div>
                    </motion.section>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </section>

        {/* Back to Home */}
        <div className="pt-16 text-center">
          <a href="/" className="text-primary hover:text-white transition-colors font-medium flex items-center justify-center gap-2">
            <ChevronLeft size={16} /> Back to Home
          </a>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default Privacy;